import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import RevealOnScroll from "./RevealOnScroll";

/**
 * CaseStudyHero — componente riusabile per l'hero dei case study.
 *
 * Layout: full-bleed image background (con fallback automatico al solido
 * dodgers se il file immagine non esiste) + overlay 35% per leggibilità.
 *
 * Props:
 *  - imagePath:     "/images/works/{slug}/hero.jpg" (auto fallback dodgers)
 *  - imageAlt:      "AC Milan Connect"
 *  - eyebrow:       "● CASE STUDY · BUSINESS IMPACT"
 *  - titleLines:    ["AC Milan Connect", "Una SIM, una community,", "una STRATEGIA."]
 *  - highlightWord: "STRATEGIA." (parola dell'ultima riga in lime)
 *  - subtitle:      "La prima SIM dedicata..."
 *  - meta:          "2023  ·  Cliente — AC Milan  ·  Format — ..."
 */
const CaseStudyHero = ({
  imagePath,
  imageAlt,
  eyebrow,
  titleLines = [],
  highlightWord = "",
  subtitle,
  meta,
  overlayOpacity = 0.35,
}) => {
  const [imgFailed, setImgFailed] = useState(false);

  const renderTitle = () =>
    titleLines.map((line, idx) => {
      const isLast = idx === titleLines.length - 1;
      if (isLast && highlightWord && line.includes(highlightWord)) {
        const parts = line.split(highlightWord);
        return (
          <span key={idx} className="block">
            {parts[0]}
            <span className="text-lime">{highlightWord}</span>
            {parts[1]}
          </span>
        );
      }
      return (
        <span key={idx} className="block">
          {line}
        </span>
      );
    });

  return (
    <section
      className="relative w-full overflow-hidden bg-dodgers"
      style={{ minHeight: "78vh" }}
      data-testid="case-study-hero"
    >
      {/* Background image con fallback automatico al solido dodgers */}
      {!imgFailed && (
        <img
          src={imagePath}
          alt={imageAlt}
          onError={() => setImgFailed(true)}
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
      )}
      {/* Overlay scuro per contrasto testo (default 35%, sovrascrivibile via prop) */}
      <div
        className="absolute inset-0 bg-dodgers"
        style={{ opacity: imgFailed ? 0 : overlayOpacity }}
        aria-hidden="true"
      />

      <div className="relative z-10 container-os pt-40 pb-16 md:pt-52 md:pb-24 min-h-[78vh] flex flex-col">
        <RevealOnScroll>
          <Link
            to="/works"
            className="inline-flex items-center gap-2 text-galactico/85 hover:text-lime transition-colors font-condensed uppercase tracking-widest text-sm"
            data-testid="back-to-works"
          >
            <ArrowLeft size={16} /> Tutti i casi studio
          </Link>
        </RevealOnScroll>

        <div className="mt-auto pt-12 md:pt-16">
          <RevealOnScroll delay={0.05}>
            <p className="font-condensed uppercase tracking-widest text-lime text-[13px]">
              {eyebrow}
            </p>
          </RevealOnScroll>

          <RevealOnScroll delay={0.12}>
            <h1
              className="font-display text-galactico mt-6 leading-[0.95]"
              style={{
                fontSize: "clamp(40px, 7vw, 88px)",
                letterSpacing: "-0.015em",
              }}
              data-testid="hero-title"
            >
              {renderTitle()}
            </h1>
          </RevealOnScroll>

          {subtitle && (
            <RevealOnScroll delay={0.2}>
              <p
                className="mt-7 text-[18px] md:text-[20px] leading-[1.5] text-galactico/90 max-w-[760px]"
                style={{
                  fontFamily: "'Hanken Grotesk', sans-serif",
                  fontStyle: "italic",
                }}
              >
                {subtitle}
              </p>
            </RevealOnScroll>
          )}

          {meta && (
            <RevealOnScroll delay={0.28}>
              <p className="mt-8 font-condensed uppercase tracking-widest text-[13px] text-galactico/85">
                {meta}
              </p>
            </RevealOnScroll>
          )}
        </div>
      </div>
    </section>
  );
};

export default CaseStudyHero;
